import React, { useState, useEffect } from "react";
import {
  Button,
  Col,
  Form,
  Row,
  Spinner,
  Table,
  Container,
} from "react-bootstrap";
import { useAuth } from "../context/AuthContext";
import { useLeagueMembership } from "../context/LeagueMembershipContext";
import ModalDialog from "../Common/ModalDialog";
import { useRequest } from "../hooks/useRequest";
import { getTeamsUrl, getTeamUrl, getSeasonUrl } from "../config/api";
import { useLeague } from "../hooks/useLeague";
import { apiPost, apiPatch, apiDelete } from "../utils/apiClient";
import { useParams, Link } from "react-router-dom";
import { PaginatedResponse } from "../types/api";
import ColorPicker from "../Common/ColorPicker";
import TeamDisplay from "../Common/TeamDisplay";

// Type Definitions
interface Team {
  id: number;
  name: string;
  season_id: number;
  colour_code?: string;
  created_at: string;
  updated_at: string;
  discarded_at: string | null;
}

interface TeamFormElement extends HTMLFormElement {
  nameField: HTMLInputElement;
}

type SortField = "name";
type SortDirection = "asc" | "desc";

// Helper Components
function TableLoadingView() {
  return (
    <Container>
      <Row className="justify-content-md-center">
        <Col md="auto">
          <Spinner animation="grow" /> Loading...
        </Col>
      </Row>
    </Container>
  );
}

// Sorting utility function
function sortTeams(
  teams: Team[],
  field: SortField,
  direction: SortDirection,
): Team[] {
  const sorted = [...teams].sort((a, b) => {
    const aVal = a[field].toLowerCase();
    const bVal = b[field].toLowerCase();

    if (aVal < bVal) return direction === "asc" ? -1 : 1;
    if (aVal > bVal) return direction === "asc" ? 1 : -1;
    return 0;
  });

  return sorted;
}

// TeamsList Component
function TeamsList({
  onCreateClick,
  onEditClick,
  onDeleteClick,
  seasonId,
}: {
  onCreateClick: () => void;
  onEditClick: (team: Team) => void;
  onDeleteClick: (team: Team) => void;
  seasonId: number;
}) {
  const auth = useAuth();
  const { canManageLeague, canScoreGames } = useLeagueMembership();
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const { data: response, error } = useRequest<PaginatedResponse<Team>>(
    getTeamsUrl(seasonId),
    auth.token,
  );

  // Handle column header click for sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Sort indicator component
  const SortIndicator = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <span className="text-muted"> ⇅</span>;
    return <span>{sortDirection === "asc" ? " ▲" : " ▼"}</span>;
  };

  // Get sorted teams
  const sortedTeams = response?.data
    ? sortTeams(response.data, sortField, sortDirection)
    : [];

  return (
    <div className="admin-content">
      <Container>
        <Row>
          <Col md={8}>
            {canManageLeague && (
              <Row className="mb-2">
                <Col className="d-flex justify-content-end">
                  <Button onClick={() => onCreateClick()} variant="primary">
                    New
                  </Button>
                </Col>
              </Row>
            )}
            <Row>
              <Col>
                {error && (
                  <div className="alert alert-danger" role="alert">
                    Error loading teams: {error.message}
                  </div>
                )}
                {!response && <TableLoadingView />}
                {response &&
                  response.data.length === 0 &&
                  (canManageLeague ? (
                    <p>Click `New` to create your first team.</p>
                  ) : (
                    <p>This season has no teams yet.</p>
                  ))}
                {response && response.data.length > 0 && (
                  <Table striped bordered hover className="crud">
                    <thead>
                      <tr>
                        <th
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSort("name")}
                        >
                          Team Name
                          <SortIndicator field="name" />
                        </th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedTeams.map((team: Team) => (
                        <tr key={team.id}>
                          <td>
                            <TeamDisplay team={team} />
                          </td>
                          <td align="right">
                            <Link
                              to={`/admin/seasons/${seasonId}/teams/${team.id}/players`}
                            >
                              <Button variant="secondary" size="sm">
                                {canScoreGames
                                  ? "Manage Players"
                                  : "View Players"}
                              </Button>
                            </Link>
                            {canManageLeague && (
                              <>
                                {" "}
                                <Button
                                  size="sm"
                                  onClick={() => onEditClick(team)}
                                >
                                  Edit
                                </Button>{" "}
                                <Button
                                  size="sm"
                                  variant="danger"
                                  onClick={() => onDeleteClick(team)}
                                >
                                  Delete
                                </Button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

// TeamsFormView Component
function TeamsFormView({
  team,
  onCancelClick,
  seasonId,
}: {
  team?: Team;
  onCancelClick: () => void;
  seasonId: number;
}) {
  const auth = useAuth();
  const { currentLeague } = useLeague();
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [colourCode, setColourCode] = useState<string | undefined>(
    team?.colour_code,
  );

  const handleSubmit: React.FormEventHandler<TeamFormElement> = (event) => {
    event.preventDefault();
    const name = event.currentTarget.nameField.value.trim();

    // Validation
    if (!name || name.length === 0) {
      setErrorMessage("Team name is required");
      return;
    }

    if (name.length < 2) {
      setErrorMessage("Team name must be at least 2 characters");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    const isEdit = !!team?.id;
    const url = isEdit && team ? getTeamUrl(team.id) : getTeamsUrl(seasonId);

    const apiFn = isEdit ? apiPatch : apiPost;
    const requestBody = {
      team: {
        name: name,
        colour_code: colourCode,
      },
    };

    apiFn(url, requestBody, {
      token: auth.token,
      ...(currentLeague?.abbr ? { leagueAbbr: currentLeague.abbr } : {}),
    })
      .then((result) => {
        console.debug("Team saved successfully:", result);
        onCancelClick(); // Returns to list view
      })
      .catch((error) => {
        console.error("Error saving team:", error);
        setErrorMessage(
          error.message || "An error occurred while saving the team",
        );
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div className="admin-content">
      <Container>
        <Row>
          <Col md={8}>
            <h1>{team ? "Edit Team" : "New Team"}</h1>

            {errorMessage && (
              <div className="alert alert-danger" role="alert">
                {errorMessage}
              </div>
            )}

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3" controlId="nameField">
                <Form.Label>Team Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Team Name"
                  defaultValue={team?.name}
                  disabled={isSubmitting}
                  required
                />
              </Form.Group>
              <ColorPicker
                value={colourCode}
                onChange={setColourCode}
                label="Team Color (optional)"
              />
              <Button
                onClick={() => onCancelClick()}
                variant="secondary"
                disabled={isSubmitting}
              >
                Cancel
              </Button>{" "}
              <Button variant="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />{" "}
                    Saving...
                  </>
                ) : (
                  "Submit"
                )}
              </Button>
            </Form>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

// Main Component
function AdminSeasonTeamsPage() {
  const auth = useAuth();
  const { currentLeague } = useLeague();
  const { seasonId } = useParams();
  const [content, setContent] = useState<React.ReactNode>(null);

  const seasonIdNum = seasonId ? parseInt(seasonId, 10) : 0;

  const { data: season } = useRequest<{ id: number; name: string }>(
    seasonId ? getSeasonUrl(seasonIdNum) : undefined,
    auth.token,
  );

  const seasonName = season?.name || `Season ${seasonId}`;

  // Navigation functions
  function showForm(team?: Team) {
    setContent(
      <TeamsFormView
        team={team}
        onCancelClick={showList}
        seasonId={seasonIdNum}
      />,
    );
  }

  function showList() {
    setContent(
      <TeamsList
        onCreateClick={showForm}
        onEditClick={showForm}
        onDeleteClick={confirmDelete}
        seasonId={seasonIdNum}
      />,
    );
  }

  function confirmDelete(team: Team) {
    setContent(
      <div
        className="modal show"
        style={{ display: "block", position: "initial" }}
      >
        <ModalDialog
          titleText="Delete Team Confirmation"
          bodyText={`Are you sure you want to delete this team? (${team.name})`}
          okText="Delete"
          onCancelClick={() => {
            console.debug("Delete canceled");
            showList();
          }}
          onSaveClick={() => {
            console.debug("Deleting team:", team);

            apiDelete(getTeamUrl(team.id), {
              token: auth.token,
              ...(currentLeague?.abbr
                ? { leagueAbbr: currentLeague.abbr }
                : {}),
            })
              .then(() => {
                console.debug("Team deleted successfully");
                showList();
              })
              .catch((error) => {
                console.error("Error deleting team:", error);
                showList();
              });
          }}
        />
      </div>,
    );
  }

  // Initialize with list view when currentLeague or seasonId changes
  useEffect(() => {
    if (seasonIdNum > 0) {
      showList();
    }
    // showList is intentionally not in deps to avoid recreating on every render
    // We only want to reset the list when currentLeague or seasonId changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLeague, seasonId]);

  return (
    <div className="mt-4">
      <h3 className="mb-4">{seasonName} Teams & Players</h3>
      {content}
    </div>
  );
}

export default AdminSeasonTeamsPage;
